import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useMutation, useQuery, useQueryClient } from "react-query";
import client from "../../../API";
import { useNavigate } from "react-router-dom";

const PROMO_TYPES = [
  { key: "campaign_followup", label: "Campaign follow‑ups" },
  { key: "reminder", label: "General reminders" },
  { key: "special_offer", label: "Special offers" },
  { key: "event_notification", label: "Events & webinars" },
];

const NEWS_TYPES = [
  { key: "weekly_newsletter", label: "Weekly newsletter" },
  { key: "monthly_newsletter", label: "Monthly newsletter" },
];

const NotifSettings = () => {
  const qc = useQueryClient();
  const navigate = useNavigate();  

  const { data, isFetching, error } = useQuery(
    ["emailSettings"],
    () => client.users.fetchEmailSettings(),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  const defaultPromo = PROMO_TYPES.map((item) => item.key);
  const defaultNews = NEWS_TYPES.map((item) => item.key);

  const [promo, setPromo] = useState(defaultPromo);
  const [news, setNews] = useState(defaultNews);
  const [frequency, setFrequency] = useState("weekly");

  useEffect(() => {
    if (!data) return;
    console.log("Fetched email settings:", data);
    setPromo(
      Array.isArray(data.promotional_types) && data.promotional_types.length
        ? data.promotional_types
        : defaultPromo
    );
    setNews(
      Array.isArray(data.newsletter_types) && data.newsletter_types.length
        ? data.newsletter_types
        : defaultNews
    );
    setFrequency(data.email_frequency || "weekly");
  }, [data]);

  const toggleItem = useCallback(
    (setter) => (key) =>
      setter((prev) =>
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
      ),
    []
  );

  const toggleAllPromo = () => {
    setPromo(promo.length === defaultPromo.length ? [] : defaultPromo);
  };

  const toggleAllNews = () => {
    setNews(news.length === defaultNews.length ? [] : defaultNews);
  };

  const saveMutation = useMutation(
    (payload) =>
      data?.global_user_id
        ? client.users.updateEmailSettings(payload)
        : client.users.createEmailSettings(payload),
    {
      onSuccess: () => {
        console.log("Settings saved successfully");
        qc.invalidateQueries("emailSettings");
      },
      onError: (err) => {
        console.error("Save error:", err);
      },
    }
  );

  const handleSave = () => {
    const payload = {
      promotional_types: promo,
      newsletter_types: news,
      email_frequency: frequency,
    };
    console.log("Saving payload:", payload);
    saveMutation.mutate(payload);
  };

  if (error) {
    console.error("Query error:", error);
    return <Typography color="error">Error loading settings: {error.message}</Typography>;
  }

  if (isFetching && !data) return <CircularProgress />;

  return (
    <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
      <CardContent>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
          Notification Preferences
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="subtitle1">Promotional & Reminders</Typography>
              <Button size="small" onClick={toggleAllPromo}>
                {promo.length === defaultPromo.length ? "Unselect All" : "Select All"}
              </Button>
            </Box>
            <FormGroup>
              {PROMO_TYPES.map(({ key, label }) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      checked={promo.includes(key)}
                      onChange={() => toggleItem(setPromo)(key)}
                    />
                  }
                  label={label}
                />
              ))}
            </FormGroup>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="subtitle1">Newsletters</Typography>
              <Button size="small" onClick={toggleAllNews}>
                {news.length === defaultNews.length ? "Unselect All" : "Select All"}
              </Button>
            </Box>
            <FormGroup>
              {NEWS_TYPES.map(({ key, label }) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      checked={news.includes(key)}
                      onChange={() => toggleItem(setNews)(key)}
                    />
                  }
                  label={label}
                />
              ))}
            </FormGroup>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Email frequency"
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sx={{ textAlign: "right" }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saveMutation.isLoading}
            >
              {saveMutation.isLoading ? (
                <CircularProgress size={24} />
              ) : (
                "Save Preferences"
              )}
            </Button>
          </Grid>
          <Grid item xs={12} sx={{ mt: 2 }}>
          <Button
            variant="text"
            color="error"
            onClick={() => navigate("/delete_account")}
          >
            Delete account
          </Button>
        </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default NotifSettings;
export const NotifTabIcon = NotificationsIcon;